// Article Search and Filtering Utilities
import { getAllArticles, ContentfulArticle } from './contentful';
import { ARTICLE_CATEGORIES, extractCategoryFromTags, suggestCategory } from './articleCategories';

export interface SearchFilters {
  query?: string;
  category?: string;
  tags?: string[];
  dateRange?: {
    start?: string;
    end?: string;
  };
  sortBy?: 'date' | 'title' | 'relevance';
  sortOrder?: 'asc' | 'desc';
}

export interface SearchResult {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  publishDate: string;
  tags: string[];
  category?: any;
  relevanceScore?: number;
  featuredImage?: any;
  author?: any;
}

// Simple text relevance scoring
function calculateRelevanceScore(searchQuery: string, article: ContentfulArticle): number {
  if (!searchQuery) return 0;
  
  const query = searchQuery.toLowerCase();
  const title = article.title?.toLowerCase() || '';
  const excerpt = article.excerpt?.toLowerCase() || '';
  const tags = article.tags?.map((tag: string) => tag.toLowerCase()) || [];
  
  let score = 0;
  
  // Title matches get highest score
  if (title.includes(query)) score += 10;
  
  // Exact title match gets even higher score
  if (title === query) score += 20;
  
  // Excerpt matches get medium score
  if (excerpt.includes(query)) score += 5;
  
  // Tag matches get good score
  if (tags.some((tag: string) => tag.includes(query))) score += 8;
  
  // Word boundary matches get bonus points
  const wordBoundaryRegex = new RegExp(`\\b${query}\\b`, 'i');
  if (wordBoundaryRegex.test(title)) score += 5;
  if (wordBoundaryRegex.test(excerpt)) score += 3;
  
  return score;
}

// Filter articles by date range
function filterByDateRange(articles: ContentfulArticle[], dateRange?: { start?: string; end?: string }): ContentfulArticle[] {
  if (!dateRange?.start && !dateRange?.end) return articles;
  
  return articles.filter(article => {
    const publishDate = new Date(article.publishDate);
    const startDate = dateRange.start ? new Date(dateRange.start) : null;
    const endDate = dateRange.end ? new Date(dateRange.end) : null;
    
    if (startDate && publishDate < startDate) return false;
    if (endDate && publishDate > endDate) return false;
    
    return true;
  });
}

// Main search function
export async function searchArticles(filters: SearchFilters = {}): Promise<SearchResult[]> {
  try {
    let articles: (ContentfulArticle & { relevanceScore?: number })[] = await getAllArticles();
    
    // Apply search query filter
    if (filters.query) {
      articles = articles
        .map((article: ContentfulArticle) => ({
          ...article,
          relevanceScore: calculateRelevanceScore(filters.query!, article)
        }))
        .filter((article: ContentfulArticle & { relevanceScore?: number }) => (article.relevanceScore || 0) > 0);
    }
    
    // Apply category filter
    if (filters.category) {
      articles = articles.filter(article => {
        const articleCategory = extractCategoryFromTags(article.tags);
        return articleCategory?.slug === filters.category;
      });
    }
    
    // Apply tags filter
    if (filters.tags && filters.tags.length > 0) {
      articles = articles.filter(article => {
        if (!article.tags) return false;
        return filters.tags!.some(filterTag => 
          article.tags!.some((articleTag: string) => 
            articleTag.toLowerCase().includes(filterTag.toLowerCase())
          )
        );
      });
    }
    
    // Apply date range filter
    articles = filterByDateRange(articles, filters.dateRange);
    
    // Sort articles
    articles.sort((a: ContentfulArticle & { relevanceScore?: number }, b: ContentfulArticle & { relevanceScore?: number }) => {
      switch (filters.sortBy) {
        case 'title':
          const comparison = a.title.localeCompare(b.title);
          return filters.sortOrder === 'desc' ? -comparison : comparison;
          
        case 'relevance':
          if (filters.query) {
            const scoreA = a.relevanceScore || 0;
            const scoreB = b.relevanceScore || 0;
            return filters.sortOrder === 'asc' ? scoreA - scoreB : scoreB - scoreA;
          }
          // Fall through to date sorting if no query
          
        case 'date':
        default:
          const dateA = new Date(a.publishDate);
          const dateB = new Date(b.publishDate);
          return filters.sortOrder === 'asc' 
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
      }
    });
    
    // Transform to SearchResult format
    return articles.map((article: ContentfulArticle & { relevanceScore?: number }) => ({
      id: article.id || '',
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt,
      publishDate: article.publishDate,
      tags: article.tags || [],
      category: extractCategoryFromTags(article.tags),
      relevanceScore: article.relevanceScore,
      featuredImage: article.featuredImage,
      author: article.author
    }));
    
  } catch (error) {
    console.error('Error searching articles:', error);
    return [];
  }
}

// Get articles by category
export async function getArticlesByCategory(categorySlug: string): Promise<SearchResult[]> {
  return searchArticles({ category: categorySlug, sortBy: 'date', sortOrder: 'desc' });
}

// Get related articles based on tags and category
export async function getRelatedArticles(currentArticleSlug: string, limit: number = 3): Promise<SearchResult[]> {
  try {
    const allArticles = await getAllArticles();
    const currentArticle = allArticles.find(article => article.slug === currentArticleSlug);
    
    if (!currentArticle) return [];
    
    const currentCategory = extractCategoryFromTags(currentArticle.tags);
    const currentTags = currentArticle.tags || [];
    
    // Calculate similarity scores for other articles
    const relatedArticles = allArticles
      .filter(article => article.slug !== currentArticleSlug)
      .map((article: ContentfulArticle) => {
        let score = 0;
        const articleCategory = extractCategoryFromTags(article.tags);
        const articleTags = article.tags || [];
        
        // Same category gets bonus points
        if (currentCategory && articleCategory && currentCategory.id === articleCategory.id) {
          score += 10;
        }
        
        // Shared tags get points
        const sharedTags = currentTags.filter(tag => 
          articleTags.some(articleTag => articleTag.toLowerCase() === tag.toLowerCase())
        );
        score += sharedTags.length * 5;
        
        // Recent articles get slight bonus
        const daysSincePublish = (Date.now() - new Date(article.publishDate).getTime()) / (1000 * 60 * 60 * 24);
        if (daysSincePublish < 30) score += 2;
        if (daysSincePublish < 7) score += 1;
        
        return {
          ...article,
          similarityScore: score
        };
      })
      .filter((article: ContentfulArticle & { similarityScore: number }) => article.similarityScore > 0)
      .sort((a: ContentfulArticle & { similarityScore: number }, b: ContentfulArticle & { similarityScore: number }) => 
        b.similarityScore - a.similarityScore
      )
      .slice(0, limit);
    
    // Transform to SearchResult format
    return relatedArticles.map((article: ContentfulArticle) => ({
      id: article.id || '',
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt,
      publishDate: article.publishDate,
      tags: article.tags || [],
      category: extractCategoryFromTags(article.tags),
      featuredImage: article.featuredImage,
      author: article.author
    }));
    
  } catch (error) {
    console.error('Error getting related articles:', error);
    return [];
  }
}

// Get popular tags across all articles
export async function getPopularTags(limit: number = 10): Promise<{ tag: string; count: number }[]> {
  try {
    const articles = await getAllArticles();
    const tagCounts: { [key: string]: number } = {};
    
    articles.forEach((article: ContentfulArticle) => {
      if (article.tags) {
        article.tags.forEach((tag: string) => {
          const normalizedTag = tag.toLowerCase().trim();
          tagCounts[normalizedTag] = (tagCounts[normalizedTag] || 0) + 1;
        });
      }
    });
    
    return Object.entries(tagCounts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
      
  } catch (error) {
    console.error('Error getting popular tags:', error);
    return [];
  }
}

// Search suggestions based on existing content
export async function getSearchSuggestions(query: string, limit: number = 5): Promise<string[]> {
  try {
    if (!query || query.length < 2) return [];
    
    const articles = await getAllArticles();
    const suggestions = new Set<string>();
    
    // Extract potential suggestions from titles and tags
    articles.forEach((article: ContentfulArticle) => {
      // Check if title contains the query
      if (article.title.toLowerCase().includes(query.toLowerCase())) {
        suggestions.add(article.title);
      }
      
      // Check tags
      if (article.tags) {
        article.tags.forEach((tag: string) => {
          if (tag.toLowerCase().includes(query.toLowerCase())) {
            suggestions.add(tag);
          }
        });
      }
    });
    
    // Add category names if they match
    ARTICLE_CATEGORIES.forEach(category => {
      if (category.name.toLowerCase().includes(query.toLowerCase()) ||
          category.slug.includes(query.toLowerCase())) {
        suggestions.add(category.name);
      }
    });
    
    return Array.from(suggestions)
      .slice(0, limit)
      .sort((a, b) => a.length - b.length); // Prefer shorter matches
      
  } catch (error) {
    console.error('Error getting search suggestions:', error);
    return [];
  }
}
